import React from 'react';
import {
  Alert, Box, Button, CircularProgress, Grid, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';

import ExecutiveCardsPanel from './ExecutiveCardsPanel';
import ScenarioComparePanel from './ScenarioComparePanel';
import WaterfallPanel from './WaterfallPanel';
import TornadoPanel from './TornadoPanel';
import SnapshotDialog from './SnapshotDialog';

import { BieMetricRow, BieScenarioOption, BieTornadoRow, BieWaterfallRow } from './types';
import { fetchCards, fetchCompare, fetchScenarios, fetchTornado, fetchWaterfall, saveSnapshot } from './bieExecutiveApi';

const ExecutiveWorkbench: React.FC<any> = (props) => {
  const [scenarios, setScenarios] = React.useState<BieScenarioOption[]>([]);
  const [baseScenario, setBaseScenario] = React.useState('');
  const [targetScenario, setTargetScenario] = React.useState('');
  const [cards, setCards] = React.useState<BieMetricRow[]>([]);
  const [compare, setCompare] = React.useState<BieMetricRow[]>([]);
  const [waterfall, setWaterfall] = React.useState<BieWaterfallRow[]>([]);
  const [tornado, setTornado] = React.useState<BieTornadoRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [snapshotOpen, setSnapshotOpen] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const data = await fetchScenarios();
        setScenarios(data);
        if (data.length > 0) {
          setBaseScenario(data[0].code);
          setTargetScenario(data.length > 1 ? data[1].code : data[0].code);
        }
      } catch (err: any) {
        setError(err.message || String(err));
      }
    })();
  }, []);

  const run = async () => {
    if (!baseScenario || !targetScenario) return;
    setLoading(true);
    setError('');
    try {
      const [cardsData, compareData, waterfallData, tornadoData] = await Promise.all([
        fetchCards(targetScenario),
        fetchCompare(baseScenario, targetScenario),
        fetchWaterfall(baseScenario, targetScenario),
        fetchTornado(targetScenario),
      ]);
      setCards(cardsData);
      setCompare(compareData);
      setWaterfall(waterfallData);
      setTornado(tornadoData);
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (baseScenario && targetScenario) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseScenario, targetScenario]);

  const handleSnapshot = async (name: string, description: string) => {
    await saveSnapshot(targetScenario, name, description);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>BIE Executive Workbench</Typography>
          <Typography variant="body2" color="text.secondary">
            Scenario intelligence, financial movement analysis and sensitivity impact.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={run} disabled={loading}>Compare</Button>
          <Button variant="contained" onClick={() => setSnapshotOpen(true)} disabled={!targetScenario}>Snapshot</Button>
        </Stack>
      </Stack>

      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size = {{ xs:2, md:6}}>
            <TextField select label="Base Scenario" value={baseScenario} onChange={(e) => setBaseScenario(e.target.value)} fullWidth>
              {scenarios.map((s) => <MenuItem key={s.code} value={s.code}>{s.code} - {s.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size = {{ xs:2, md:6}}>
            <TextField select label="Target Scenario" value={targetScenario} onChange={(e) => setTargetScenario(e.target.value)} fullWidth>
              {scenarios.map((s) => <MenuItem key={s.code} value={s.code}>{s.code} - {s.name}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {loading && <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}><CircularProgress size={22} /><Typography>Computing executive analytics...</Typography></Stack>}

      <Stack spacing={3}>
        <ExecutiveCardsPanel cards={cards} />
        <ScenarioComparePanel rows={compare} />
        <Grid container spacing={3}>
          <Grid size = {{ xs:2, lg:6}}><WaterfallPanel rows={waterfall} /></Grid>
          <Grid size = {{ xs:2, lg:6}}><TornadoPanel rows={tornado} /></Grid>
        </Grid>
      </Stack>

      <SnapshotDialog open={snapshotOpen} onClose={() => setSnapshotOpen(false)} onSave={handleSnapshot} />
    </Box>
  );
};

export default ExecutiveWorkbench;
